import type { ConsoleEntry, SessionEntry } from '@mkcore/dev/schema';
import type { FEEDBACK_AREAS } from '@mkcore/dev/schema';

/**
 * What a feedback report can carry besides the words (CO-0398): the browser's recent console messages and the
 * session (pages visited, controls pressed, API calls with their status). Both live in memory only, in two small
 * rolling lists; nothing leaves the browser unless someone sends feedback with "technical details" switched on.
 * Request and response bodies are never recorded; links with tokens or codes are scrubbed here and again on the server.
 */
const MAX_CONSOLE = 100;
const MAX_SESSION = 150;
const consoleLog: ConsoleEntry[] = [];
const sessionLog: SessionEntry[] = [];
const now = () => new Date().toISOString();

function push<T>(list: T[], entry: T, max: number) {
  list.push(entry);
  if (list.length > max) list.splice(0, list.length - max);
}

/** Links and messages without tokens, codes or keys. */
export function scrub(s: string): string {
  return s
    .replace(/([?&](?:token|code|state|key|secret|password|sig|signature|access_token|id_token|session)=)[^&#\s"']+/gi, '$1…')
    .replace(/(Bearer\s+)[A-Za-z0-9._~+/=-]{8,}/gi, '$1…')
    .replace(/\bmkc_[A-Za-z0-9_-]{6,}/g, 'mkc_…');
}

function asText(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack.split('\n').slice(1, 6).join('\n') : ''}`;
  if (typeof arg === 'string') return arg;
  try {
    return JSON.stringify(arg) ?? String(arg);
  } catch {
    return String(arg);
  }
}

function record(level: ConsoleEntry['level'], source: string, args: unknown[]) {
  push(consoleLog, { t: now(), level, source, message: scrub(args.map(asText).join(' ')).slice(0, 2000) }, MAX_CONSOLE);
}

let installed = false;

/** Starts recording; called once at app start. Never throws, never breaks logging. */
export function installFeedbackCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  for (const level of ['error', 'warn', 'info', 'log'] as const) {
    // eslint-disable-next-line no-console -- the capture wraps the console on purpose
    const original = console[level].bind(console);
    // eslint-disable-next-line no-console
    console[level] = (...args: unknown[]) => {
      try {
        record(level, 'console', args);
      } catch {
        /* recording must never break logging */
      }
      original(...args);
    };
  }
  window.addEventListener('error', (e) => {
    record('error', 'uncaught', [e.error ?? e.message]);
    push(sessionLog, { t: now(), kind: 'error', label: String(e.message).slice(0, 120) }, MAX_SESSION);
  });
  window.addEventListener('unhandledrejection', (e) => record('error', 'promise', [e.reason]));

  // The pages visited: the router uses history.pushState / replaceState.
  const visit = () => push(sessionLog, { t: now(), kind: 'nav', path: scrub(location.pathname + location.search).slice(0, 500) }, MAX_SESSION);
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method].bind(history);
    history[method] = (...args: Parameters<History['pushState']>) => {
      const before = location.pathname + location.search;
      original(...args);
      if (location.pathname + location.search !== before) visit();
    };
  }
  window.addEventListener('popstate', visit);
  visit();

  // What was pressed: the control's visible name only, never what was typed.
  document.addEventListener(
    'click',
    (e) => {
      const el = (e.target as Element | null)?.closest?.('button, a, [role="button"], [role="tab"], [role="menuitem"], [role="option"], input[type="checkbox"], input[type="radio"]');
      if (!el) return;
      const name = (el.getAttribute('aria-label') || (el as HTMLElement).innerText || el.getAttribute('title') || '').trim().replace(/\s+/g, ' ').slice(0, 80);
      push(sessionLog, { t: now(), kind: 'click', label: scrub(`${el.getAttribute('role') ?? el.tagName.toLowerCase()} “${name}”`).slice(0, 200) }, MAX_SESSION);
    },
    { capture: true },
  );

  // Calls to the API: method, path, status and how long they took. The feedback calls themselves are left out.
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    let path: string | null = null;
    try {
      const u = new URL(url, location.href);
      if (u.origin === location.origin && u.pathname.startsWith('/api/') && !u.pathname.startsWith('/api/v1/dev/feedback')) path = scrub(u.pathname + u.search).slice(0, 500);
    } catch {
      /* not a URL we follow */
    }
    const start = performance.now();
    try {
      const res = await originalFetch(input, init);
      if (path) push(sessionLog, { t: now(), kind: 'api', method, path, status: res.status, ms: Math.round(performance.now() - start) }, MAX_SESSION);
      return res;
    } catch (err) {
      if (path) push(sessionLog, { t: now(), kind: 'api', method, path, status: 0, ms: Math.round(performance.now() - start) }, MAX_SESSION);
      throw err;
    }
  };
}

export interface Snapshot {
  console: ConsoleEntry[];
  session: SessionEntry[];
  context: Record<string, string | number | boolean>;
}

/** What a report would carry right now. */
export function snapshot(): Snapshot {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  return {
    console: [...consoleLog],
    session: [...sessionLog],
    context: {
      url: scrub(location.href).slice(0, 1000),
      path: scrub(location.pathname + location.search).slice(0, 500),
      userAgent: navigator.userAgent.slice(0, 500),
      platform: nav.userAgentData?.platform || navigator.platform || '',
      language: navigator.language,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen.width}x${window.screen.height}`,
      pixelRatio: window.devicePixelRatio,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: navigator.onLine,
      appVersion: String(import.meta.env.VITE_APP_VERSION || 'dev').slice(0, 12),
      colorScheme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
    },
  };
}

export const canCaptureScreen = (): boolean => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

/** One still image of a screen, window or tab the person picks (the browser asks first). */
export async function captureScreen(): Promise<Blob> {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
  } as DisplayMediaStreamOptions);
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((r) => setTimeout(r, 300)); // the first frame, after the browser's own sharing prompt is gone
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    const png = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));
    if (png && png.size <= 7 * 1024 * 1024) return png;
    const jpeg = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
    if (!jpeg) throw new Error('The screenshot could not be made');
    return jpeg;
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
}

type Area = (typeof FEEDBACK_AREAS)[number];

/** Which package a path belongs to, as the area of a report (docs/01-architectuur/03-bouwstenen.md). */
export function areaFor(path: string): Area {
  const rules: [RegExp, Area][] = [
    [/^\/(admin|settings|login|consent)(\/|$)|^\/$/, 'platform'],
    [/^\/feedback/, 'dev'],
    [/^\/(organisations|contacts|crm|leads|pipeline)/, 'crm'],
    [/^\/engagements/, 'engagements'],
    [/^\/projects/, 'projects'],
    [/^\/time/, 'time'],
    [/^\/(billing|invoices|expenses)/, 'billing'],
    [/^\/(dev|issues|boards)/, 'dev'],
    [/^\/reports/, 'reporting'],
    [/^\/proposals/, 'proposals'],
    [/^\/support/, 'support'],
    [/^\/portal/, 'portal'],
    [/^\/(files|documents)/, 'files'],
    [/^\/(inbox|notifications)/, 'collab'],
    [/^\/approvals/, 'approvals'],
    [/^\/knowledge/, 'knowledge'],
    [/^\/agents/, 'agents'],
  ];
  return rules.find(([rx]) => rx.test(path))?.[1] ?? 'other';
}
